import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { X, Loader2 } from 'lucide-react';
import { RateSlab } from '../../services/roomService';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function maskToChecked(mask: number): boolean[] {
  return DAY_LABELS.map((_, i) => Boolean((mask >> i) & 1));
}

function checkedToMask(checked: boolean[]): number {
  let m = 0;
  checked.forEach((c, i) => {
    if (c) m |= 1 << i;
  });
  return m;
}

function popcount(n: number): number {
  let c = 0;
  let x = n;
  while (x) {
    c += x & 1;
    x >>= 1;
  }
  return c;
}

interface RateSlabModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<RateSlab>) => Promise<void>;
  rateSlab?: RateSlab | null;
  roomPlanName?: string;
}

const RateSlabModal: React.FC<RateSlabModalProps> = ({
  isOpen,
  onClose,
  onSave,
  rateSlab,
  roomPlanName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dayChecked, setDayChecked] = useState<boolean[]>(() => maskToChecked(31));
  const [formData, setFormData] = useState<Partial<RateSlab>>({
    valid_from: '',
    valid_to: '',
    rate_per_night: 0,
    weekend_rate_per_night: undefined,
    slab_name: '',
    slab_description: '',
    status: 'active'
  });

  useEffect(() => {
    if (rateSlab) {
      const mask =
        rateSlab.weekday_day_mask != null && rateSlab.weekday_day_mask !== undefined
          ? Number(rateSlab.weekday_day_mask)
          : 31;
      setDayChecked(maskToChecked(mask));
      setFormData({
        valid_from: rateSlab.valid_from.split('T')[0],
        valid_to: rateSlab.valid_to.split('T')[0],
        rate_per_night: rateSlab.rate_per_night,
        weekend_rate_per_night:
          rateSlab.weekend_rate_per_night != null
            ? rateSlab.weekend_rate_per_night
            : undefined,
        slab_name: rateSlab.slab_name || '',
        slab_description: rateSlab.slab_description || '',
        status: rateSlab.status
      });
    } else {
      setDayChecked(maskToChecked(31));
      setFormData({
        valid_from: '',
        valid_to: '',
        rate_per_night: 0,
        weekend_rate_per_night: undefined,
        slab_name: '',
        slab_description: '',
        status: 'active'
      });
    }
  }, [rateSlab]);

  const uncheckedDayNames = useMemo(() => {
    return DAY_LABELS.filter((_, i) => !dayChecked[i]).join(', ');
  }, [dayChecked]);

  const toggleDay = (index: number) => {
    setDayChecked((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const mask = checkedToMask(dayChecked);
    const bits = popcount(mask);
    if (bits === 0 || bits === 7) {
      setError('Select at least one day for weekday rate and leave at least one day for weekend rate.');
      return;
    }

    if (!formData.valid_from || !formData.valid_to || !formData.rate_per_night) {
      setError('Please fill in all required fields');
      return;
    }

    const fromDate = new Date(formData.valid_from);
    const toDate = new Date(formData.valid_to);

    if (toDate < fromDate) {
      setError('End date must be after start date');
      return;
    }

    const weekendVal = formData.weekend_rate_per_night;
    const weekendNum =
      weekendVal === undefined || weekendVal === null
        ? null
        : typeof weekendVal === 'number'
          ? weekendVal
          : parseFloat(String(weekendVal));

    setLoading(true);
    try {
      await onSave({
        ...formData,
        weekday_day_mask: mask,
        weekend_rate_per_night:
          weekendNum !== null && !Number.isNaN(weekendNum) ? weekendNum : null,
        rate_per_night: Number(formData.rate_per_night)
      });
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save rate slab';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof RateSlab, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">
            {rateSlab ? 'Edit Rate Slab' : 'Add Rate Slab'}
            {roomPlanName && (
              <span className="text-sm font-normal text-gray-600 block">
                for {roomPlanName}
              </span>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Date Range</Label>
              <p className="text-xs text-gray-500 mb-2">
                Select the period when this rate will be applicable
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valid_from">Valid From</Label>
                  <Input
                    id="valid_from"
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => handleInputChange('valid_from', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="valid_to">Valid To</Label>
                  <Input
                    id="valid_to"
                    type="date"
                    value={formData.valid_to}
                    min={formData.valid_from}
                    onChange={(e) => handleInputChange('valid_to', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Days using weekday rate</Label>
              <p className="text-xs text-gray-500 mb-2">
                Checked days use the first rate; unchecked days use the weekend rate (or same as
                weekday if left empty).
              </p>
              <div className="flex flex-wrap gap-3">
                {DAY_LABELS.map((label, i) => (
                  <label
                    key={label}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={dayChecked[i]}
                      onChange={() => toggleDay(i)}
                      className="rounded border-gray-300"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="rate_per_night">Weekday rate per night (₹)</Label>
              <Input
                id="rate_per_night"
                type="number"
                min="0"
                step="0.01"
                value={formData.rate_per_night || ''}
                onChange={(e) =>
                  handleInputChange('rate_per_night', parseFloat(e.target.value) || 0)
                }
                required
              />
            </div>

            <div>
              {uncheckedDayNames ? (
                <Label htmlFor="weekend_rate_per_night" className="text-sm">
                  Weekend rate per night (₹){' '}
                  <span className="font-normal text-gray-600">
                    — applies to: {uncheckedDayNames}
                  </span>
                </Label>
              ) : (
                <Label htmlFor="weekend_rate_per_night">Weekend rate per night (₹)</Label>
              )}
              <Input
                id="weekend_rate_per_night"
                type="number"
                min="0"
                step="0.01"
                placeholder="Same as weekday if empty"
                value={
                  formData.weekend_rate_per_night === undefined ||
                  formData.weekend_rate_per_night === null
                    ? ''
                    : formData.weekend_rate_per_night
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    handleInputChange('weekend_rate_per_night', undefined);
                  } else {
                    handleInputChange('weekend_rate_per_night', parseFloat(v) || 0);
                  }
                }}
              />
            </div>

            <div>
              <Label htmlFor="slab_name">Slab Name</Label>
              <Input
                id="slab_name"
                placeholder="e.g., Peak Season Rate"
                value={formData.slab_name}
                onChange={(e) => handleInputChange('slab_name', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="slab_description">Description</Label>
              <Textarea
                id="slab_description"
                placeholder="Optional description for this rate slab"
                value={formData.slab_description}
                onChange={(e) => handleInputChange('slab_description', e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {rateSlab ? 'Update Rate Slab' : 'Create Rate Slab'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RateSlabModal;
